/**
 * Bundle economics: per-unit margin or subsidy for each bundle, and what a
 * planned number of units adds up to across bundles.
 *
 * Pure and framework-free, and exact: every amount is a decimal string and the
 * arithmetic goes through `utils/decimal`, never through float `Number` maths.
 * A bundle's price is in the project's currency (the project picks one when it
 * is created); its cost is the sourcing target (`targetCost`), which can be held
 * in another currency. The two are compared in the project currency: a foreign
 * cost is converted only through the caller's `convert`, and with no rate it is
 * reported as not comparable rather than being treated 1:1. A missing price or
 * cost is a named state, never a zero that would read as a free bundle.
 */

import { absDecimal, compareDecimal, multiplyDecimalByInt, percentOf, subtractDecimal, sumDecimal, toMicro } from './decimal';

export type EconomicsState =
  | 'margin'
  | 'subsidy'
  | 'breakeven'
  | 'needs-price'
  | 'needs-cost'
  | 'not-comparable';

export interface EconomicsBundleInput {
  id: string;
  name: string;
  /** Per-unit selling price, in the project currency. */
  price: number | string | null | undefined;
  /** Per-unit sourcing target, in `targetCurrency`. */
  targetCost?: number | string | null;
  /** Missing means the project currency. */
  targetCurrency?: string | null;
}

/** What the rows are compared in, and how a foreign cost gets there. */
export interface EconomicsContext {
  /** The project currency every price is in. */
  currency: string;
  /** Converts a decimal amount into `currency`, or null when no rate is known for `from`. */
  convert?: (amount: string, from: string) => string | null;
}

export interface EconomicsRow {
  id: string;
  name: string;
  state: EconomicsState;
  price: string | null;
  cost: string | null;
  costCurrency: string;
  /** The cost in the project currency when it was converted from another; else null. */
  convertedCost: string | null;
  /** price - cost per unit; negative is a subsidy. Null unless comparable. */
  marginPerUnit: string | null;
  /** marginPerUnit as a percentage of price. Null unless comparable. */
  marginPct: number | null;
  units: number | null;
  /** marginPerUnit * units. Null without units or a comparable margin. */
  total: string | null;
}

export interface NetSummary {
  /** Sum of every row total; null when no row has a total. */
  net: string | null;
  /** How many rows contributed a total. */
  counted: number;
  marginNames: string[];
  subsidyNames: string[];
}

/** A planned-units entry is valid only as a whole number of at least 1. */
export function parseUnits(raw: string | undefined): number | null {
  if (raw === undefined || raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
}

const POSITIVE = (v: number | string | null | undefined): string | null => {
  const micro = toMicro(v);
  return micro !== null && micro > BigInt(0) ? String(v).trim() : null;
};

function computeRow(bundle: EconomicsBundleInput, rawUnits: string | undefined, ctx: EconomicsContext): EconomicsRow {
  const projectCurrency = ctx.currency.trim().toUpperCase();
  const costCurrency = (bundle.targetCurrency || projectCurrency).trim().toUpperCase();
  const price = POSITIVE(bundle.price);
  const cost = POSITIVE(bundle.targetCost);
  const base = {
    id: bundle.id,
    name: bundle.name,
    price,
    cost,
    costCurrency,
    convertedCost: null,
    marginPerUnit: null,
    marginPct: null,
    units: parseUnits(rawUnits),
    total: null,
  };

  if (price === null) return { ...base, state: 'needs-price' };
  if (cost === null) return { ...base, state: 'needs-cost' };
  const foreign = costCurrency !== projectCurrency;
  const convertedCost = foreign ? (ctx.convert?.(cost, costCurrency) ?? null) : null;
  if (foreign && convertedCost === null) return { ...base, state: 'not-comparable' };

  const costInProject = foreign ? (convertedCost as string) : cost;
  const marginPerUnit = subtractDecimal(price, costInProject) as string;
  const sign = compareDecimal(marginPerUnit, '0');
  const state: EconomicsState = sign === 1 ? 'margin' : sign === -1 ? 'subsidy' : 'breakeven';
  return {
    ...base,
    state,
    convertedCost,
    marginPerUnit,
    marginPct: percentOf(marginPerUnit, price),
    total: base.units === null ? null : multiplyDecimalByInt(marginPerUnit, base.units),
  };
}

/** One row per bundle, in input order. `unitsById` holds the raw input text. */
export function computeBundleEconomics(
  bundles: readonly EconomicsBundleInput[],
  unitsById: Readonly<Record<string, string>>,
  ctx: EconomicsContext,
): EconomicsRow[] {
  return bundles.map(b => computeRow(b, unitsById[b.id], ctx));
}

export function summariseNet(rows: readonly EconomicsRow[]): NetSummary {
  const withTotal = rows.filter(r => r.total !== null);
  return {
    net: withTotal.length === 0 ? null : sumDecimal(withTotal.map(r => r.total)),
    counted: withTotal.length,
    marginNames: withTotal.filter(r => compareDecimal(r.total, '0') === 1).map(r => r.name),
    subsidyNames: withTotal.filter(r => compareDecimal(r.total, '0') === -1).map(r => r.name),
  };
}

const joinNames = (names: readonly string[]): string =>
  names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/**
 * Plain-language answer to "does one bundle's margin cover another's
 * subsidy". Empty unless at least one bundle has a margin and one a subsidy.
 * `formatAmount` receives the absolute shortfall as a decimal string.
 */
export function describeNet(summary: NetSummary, formatAmount: (amount: string) => string): string {
  const { net, marginNames, subsidyNames } = summary;
  if (net === null || marginNames.length === 0 || subsidyNames.length === 0) return '';
  const noun = subsidyNames.length === 1 ? 'subsidy' : 'subsidies';
  const subject = `Margin on ${joinNames(marginNames)}`;
  const target = `the ${joinNames(subsidyNames)} ${noun}`;
  if (compareDecimal(net, '0') !== -1) return `${subject} covers ${target}.`;
  return `${subject} does not cover ${target}. Shortfall: ${formatAmount(absDecimal(net) as string)}.`;
}
